// NewLaunchesPage — top-level "🚀 New Launches" browse experience.
// Pulls /api/launches with toggle for "Live" (already launched) vs
// "Upcoming" (future launch_date). Cards show launch date + a Pre-order
// button when advance_booking is enabled.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fmtMoney } from "../lib/money";
import { Rocket, CalendarClock, ShoppingBag, Star, Flame } from "lucide-react";
import { api } from "../lib/api";

const FILTERS = [
  { id: "all",      label: "All launches", icon: Rocket    },
  { id: "live",     label: "Just dropped", icon: Flame     },
  { id: "upcoming", label: "Coming soon",  icon: CalendarClock },
];

export default function NewLaunchesPage() {
  const [type, setType] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data || []))
      .catch(() => setCats([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (type !== "all") params.set("category_type", type);
    if (catId) params.set("category_id", catId);
    api.get(`/launches?${params}`)
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type, catId]);

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric"
      });
    } catch { return iso; }
  };

  return (
    <div className="pb-24 md:pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6"
      data-testid="new-launches-page">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-3xl mb-6 p-8 sm:p-12 bg-gradient-to-br from-[#FF007F]/30 via-[#00F0FF]/15 to-[#D4FF00]/30">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,240,255,0.15),transparent_60%)] pointer-events-none"/>
        <div className="relative z-10">
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-1 text-[#FF007F]">
            <Rocket size={12}/> Fresh & coming soon
          </div>
          <h1 className="font-display font-black text-4xl sm:text-6xl uppercase mt-2">
            New <span className="text-[#FF007F]">Launches</span><span className="text-[#D4FF00]">.</span>
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-2 max-w-xl">
            Be first to drop. Brand-new collections, exclusive collabs, and pre-orders
            you can lock in before they go viral.
          </p>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="launches-filter-row">
        <div className="flex gap-1.5 p-1 bg-[#261534] border border-[#3D2845] rounded-full">
          {FILTERS.map(f => {
            const I = f.icon;
            const active = type === f.id;
            return (
              <button key={f.id} onClick={() => setType(f.id)}
                data-testid={`launches-filter-${f.id}`}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                  active ? "bg-[#FF007F] text-white" : "text-[#A1A1AA] hover:text-[#FAFAFA]"
                }`}>
                <I size={12}/> {f.label}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Category</span>
          <select className="input-dark py-1 text-xs"
            value={catId} onChange={e => setCatId(e.target.value)}
            data-testid="launches-category-select">
            <option value="">All</option>
            {cats.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Grid */}
      {loading && (
        <div className="card p-12 text-center text-[#A1A1AA] text-sm">Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="card p-12 text-center" data-testid="launches-empty">
          <Rocket size={32} className="mx-auto text-[#3D2845] mb-3"/>
          <div className="font-display font-bold uppercase text-sm">No launches yet</div>
          <div className="text-xs text-[#A1A1AA] mt-1">
            Sellers tag products as "New launch" — none have done so for this filter yet.
          </div>
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
          data-testid="launches-grid">
          {items.map(p => <LaunchCard key={p.product_id} p={p} formatDate={formatDate}/>)}
        </div>
      )}
    </div>
  );
}

function LaunchCard({ p, formatDate }) {
  const launchDate = formatDate(p.launch_date);
  const shipDate = formatDate(p.expected_ship_date);
  const isUpcoming = p.launch_date && new Date(p.launch_date) > new Date();
  const ribbon = isUpcoming ? "UPCOMING" : "JUST DROPPED";
  const ribbonColor = isUpcoming ? "#00F0FF" : "#FF007F";
  return (
    <Link to={`/product/${p.product_id}`}
      data-testid={`launch-card-${p.product_id}`}
      className="card group overflow-hidden hover:border-[#FF007F]/50 transition">
      <div className="relative aspect-[3/4] bg-[#261534] overflow-hidden">
        {p.images?.[0] && (
          <img src={p.images[0]} alt={p.name}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
        )}
        <div className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded text-white"
          style={{ background: ribbonColor }}>
          {ribbon}
        </div>
        {p.is_advance_booking && (
          <div className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-black/60 text-[#D4FF00] backdrop-blur">
            📦 PRE-ORDER
          </div>
        )}
      </div>
      <div className="p-3 space-y-1">
        <div className="text-xs font-bold line-clamp-1">{p.name}</div>
        <div className="text-[10px] text-[#A1A1AA] line-clamp-1">{p.store_name}</div>
        <div className="flex items-center justify-between">
          <span className="font-display font-bold text-[#D4FF00]">{fmtMoney(p.price)}</span>
          <span className="text-[9px] text-[#A1A1AA] flex items-center gap-1">
            <Star size={9} className="text-[#D4FF00] fill-[#D4FF00]"/> {(p.rating || 5).toFixed(1)}
          </span>
        </div>
        {(launchDate || shipDate) && (
          <div className="text-[9px] uppercase tracking-widest text-[#A1A1AA] pt-1 flex items-center gap-1">
            <CalendarClock size={9}/>
            {isUpcoming
              ? <>Drops {launchDate}</>
              : shipDate
                ? <>Ships {shipDate}</>
                : <>Just dropped {launchDate}</>}
          </div>
        )}
      </div>
    </Link>
  );
}
